import React, {createContext, useContext, useMemo} from 'react'
import {invariant} from '../helpers/invariant'
import {
    TpaSuppressionData,
    TpaBalanceConfig,
    defaultTpaConfig,
    TpaConfigProviderProps
} from './tpa-config.types'

export interface TpaConfigContextType {
    // TPA suppression data (computed server-side)
    suppressionData: TpaSuppressionData
    // TPA balance configuration (fetched server-side)
    balanceConfig: TpaBalanceConfig
}

/**
 * Hook to access TPA configuration data.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { suppressionData, balanceConfig } = useTpaConfig()
 * }
 * ```
 */
export const useTpaConfig = (): TpaConfigContextType => {
    const context = useContext(TpaConfigContext)
    invariant(context, 'useTpaConfig must be used within a TpaConfigProvider')
    return context
}

export const useTpaSuppressionData = (): TpaSuppressionData => {
    const {suppressionData} = useTpaConfig()
    return suppressionData
}

export const useTpaBalanceConfig = (): TpaBalanceConfig => {
    const {balanceConfig} = useTpaConfig()
    return balanceConfig
}

const TpaConfigContext = createContext<TpaConfigContextType>(defaultTpaConfig)

export const TpaConfigProvider: React.FC<TpaConfigProviderProps> = ({
    children,
    serverSuppressionData,
    serverBalanceConfig
}) => {
    const data = useMemo(
        () => ({
            suppressionData: serverSuppressionData ?? defaultTpaConfig.suppressionData,
            balanceConfig: serverBalanceConfig ?? defaultTpaConfig.balanceConfig
        }),
        [serverSuppressionData, serverBalanceConfig]
    )

    return <TpaConfigContext.Provider value={data}>{children}</TpaConfigContext.Provider>
}
